import React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import { currySC, IFNTheme, registerClass, cns } from '@food/css-manager';
//import { Banner } from '../Banner';
//import { RowFiller } from '../ui/RowFiller';
import { nodes } from '../../utils/data';
import { DefaultLayout } from '../../utils/layout';
import { IPageData } from '../DefaultPageData';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../Spinner';
import { config } from '../../utils/config';
import { MagazineSlideshow } from '../MagazineSlideshow';
import {
	magazineCardFragment,
	magazineLastIssuesSliderQuery,
} from '../../utils/fragments';
import { SubscriptionBannerStatusActive } from '../SubscriptionBannerStatusActive';
import { uniqueQueryStringValue } from '../../utils/misc';

declare module 'react' {
	interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
		name?: string;
	}
};

const magazineCollectionDirectoryQuery = gql`
	query MagazineCollectionDirectoryQuery($filter: MagazineCollectionFilterInput!) {
		magazineCollections(filter: $filter) {
			edges {
				node {
					id
					name
					priority
					visibilityArgs
					BrandSf
					categoriesWithCounts {
						name
						count
					}
				}
			}
		}
	}
`;

const magazineCollectionSliderQuery = gql`
	query MagazineCollectionSliderQuery($filter: MagazineFilterInput!) {
		magazines(filter: $filter) {
			edges {
				node {
					...MagazineCardFragment
				}
			}
		}
	}
	${magazineCardFragment}
`;

const styleClass = registerClass(
	(t: IFNTheme) => `
	.${sc('section')} {
		padding: ${t.ratios.xl * t.font.baseSize}px 0 ${t.ratios.l * t.font.baseSize}px;
	}
 
	.${sc('stand')} {
		display: flex;

		&:first-of-type {
			margin-top: ${t.ratios.l * t.font.baseSize}px;
		}

		&:not(:last-of-type) {
			margin-bottom: ${t.ratios.l * 2 * t.font.baseSize}px;
		}

		&.${sc('layout', DefaultLayout.TABLET)},
		&.${sc('layout', DefaultLayout.MOBILE)} {
			flex-direction: column;

			&:not(:last-of-type) {
				margin-bottom: ${t.ratios.xl * 2 * t.font.baseSize}px;
			}

			.${sc('standImageContainer')} {
				width: 100%;
				flex-basis: auto;
				padding: 0;

				img {
					width: 80%;
					max-width: 200px;
				}
			}

			.${sc('standContent')} {
				width: 100%;
				flex-basis: auto;
				margin: 0;
				border-left: none;
				padding: ${t.ratios.xl * 1 * t.font.baseSize}px ${t.font.baseSize}px 0;
			}
		}
	}

	.${sc('standImageContainer')} {
		flex: 0 1 15%;
		display: flex;
		justify-content: center;
		align-items: flex-start;
		padding: ${2 *
		t.ratios
			.s}em; /* t.ratios.s serve a parificare la spaziatura con .standContent padding-left */
		
		img {
			width: 100%;
			min-width: 100px;
		}
	}

	.${sc('standContent')} {
		flex: 0 1 75%;
		padding: 2em;
		border-left: 2px solid ${t.colors.danger.plain};
		font-size: ${t.ratios.s}em;
		line-height: 1.2;

		em {
			font-style: italic;
		}
	}

	.${sc('sectionTitle')} {
		margin-bottom: ${t.ratios.s * t.font.baseSize}px;
		font-size: ${t.ratios.xl}em;
		font-weight: ${t.font.weights.bold};
	}
`,
);

const sc = currySC(styleClass);

export const Dashboard: React.FC<IPageData & { filter1?: string, filter2?: string }> = ({ layout, user, filter1, filter2 }) => {
	const { t } = useTranslation();

	return (
		<main className={styleClass}>
			<SubscriptionBannerStatusActive user={user} />
			{config.HIDE_HOME_LAST_ITEM ? (
				''
			) : (
				<>
					<a name={uniqueQueryStringValue(t`last_issues`)} className={'anchor_' + uniqueQueryStringValue(t`last_issues`)} />
					<MagazineSlideshow
						layout={layout}
						className={sc('section')}
						query={magazineLastIssuesSliderQuery}
						variables={{}}
						sectionTitle={t`last_issues`}
					/>
				</>
			)}

			<Query<any, { filter: { BrandSf: string } }>
				query={magazineCollectionDirectoryQuery}
				variables={{ filter: { BrandSf: config.BRAND_SF } }}
			>
				{({ error, loading, data }) => {
					if (error) {
						// TODO
						console.error(error);
						return null;
					}

					if (loading) {
						return <Spinner />;
					}

					const magazineCollections = nodes(data.magazineCollections).filter((collection: any) => collection.name.indexOf('Whitepapers per Landing Pages') === -1);

					// creo gli a/names
					let anchors: Record<number, any> = { 0: uniqueQueryStringValue(t`last_issues`) };
					magazineCollections.map((collection: any) => {
						const label = uniqueQueryStringValue(collection.name, Object.values(anchors));
						anchors[collection.id] = label;
					});

					// verifico la presenza del parametro "collection" e "filter" nella QS

					setTimeout(function () {
						if (filter1 || filter2) {
							// recupero l'oggetto DOM dell'ancora
							let anchors = document.getElementsByClassName('anchor_' + filter1);
							if (!anchors.length && filter2) {
								anchors = document.getElementsByClassName('anchor_' + filter2);
							}
							if (anchors.length) {
								const anchor = anchors[0];
								const bodyRect = document.body.getBoundingClientRect();
								const elemRect = anchor.getBoundingClientRect();
								// calcolo l'offset (top da scrollare)
								const offset = elemRect.top - bodyRect.top;
								const initialST = window.scrollY ? window.scrollY : 0;

								// creo una semplice animazione
								let index = 0;
								const interval = setInterval(function () {
									index++;

									// ease out quart
									const progress = 1 - Math.pow(1 - (index / 100), 4);
									window.scrollTo(0, initialST + ((offset - initialST) * progress));

									if (index > 100) {
										clearInterval(interval);
									}
								}, 10);
							}
						}
					}, 400);

					return (
						<>
							{magazineCollections.map((collection: any) => (
								<>
									<a name={anchors[collection.id] ? anchors[collection.id] : collection.name} className={'anchor_' + (anchors[collection.id] ? anchors[collection.id] : collection.name)} />
									<MagazineSlideshow
										className={sc('section')}
										layout={layout}
										query={magazineCollectionSliderQuery}
										variables={{
											filter: {
												MagazineCollection: { id: collection.id },
											},
										}}
										key={anchors[collection.id] ? anchors[collection.id] : collection.name}
										collection={collection}
										preSelectedCategories={[filter1, filter2]}
									/>
								</>
							))}
						</>
					);
				}}
			</Query>

			{config.OTHER_BRAND_SF && config.OTHER_BRAND_SF.length > 0 && (
				<section className={sc('section')}>
					<h2 className={cns(sc('sectionTitle'), sc('layout', layout))}>
						{t`other_newsstands`}
					</h2>
					{config.OTHER_BRAND_SF.map((stand: any, index: number) => (
						<div className={cns(sc('stand'), sc('layout', layout))} key={index}>
							<div className={sc('standImageContainer')}>
								<img src={stand.media} alt={'brand logo'} />
							</div>
							<div
								className={sc('standContent')}
								dangerouslySetInnerHTML={{ __html: stand.content }}
							/>
						</div>
					))}
				</section>
			)}
		</main>
	);
};
